import {Helmet} from '@dr.pogodin/react-helmet';
import {Button, openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNotNil, mergeAll} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {useDeleteRecordAuditMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {recordAuditRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  Main,
  queryParams,
  refreshDmsDatagrid,
} from '@dms/shared';

import {composePath, useNavigate, useQueryState} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const RECORD_AUDIT_HISTORY_GRID_CODE = 'record-audit';

export function RecordAuditList() {
  const navigate = useNavigate();

  const [deleteAudit] = useDeleteRecordAuditMutation();

  // Optional pre-filter (e.g. arriving from a service order's Audits tab): constrain the
  // grid to a single linked record via the `recordId` filter source.
  const [linkedRecordId] = useQueryState(queryParams.RECORD_AUDIT_LINKED_RECORD_ID);
  const queryModifier = isNotNil(linkedRecordId)
    ? (filter: QueryFilterObject) => mergeAll([filter, {recordId: linkedRecordId}])
    : undefined;

  const actionCallback: ActionCallback = ({actionKey, rowId, deselectAll}) => {
    const auditId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'detail', () => {
        if (isString(auditId)) {
          navigate(composePath(recordAuditRoutes.detail, {params: {id: auditId}}));
        }
      })
      .with('delete', () => {
        if (!isString(auditId)) {
          return;
        }
        openDeleteDialog({
          onConfirm: () =>
            deleteAudit({auditId})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshDmsDatagrid(RECORD_AUDIT_HISTORY_GRID_CODE, {deselectAll: true}))
              .then(deselectAll)
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('module.recordAudit.title')} />
      <Main
        isFullHeight
        heading={i18n.t('page.recordAudit.title')}
        actions={[
          {
            type: 'custom',
            component: (
              <Button
                data-testid="recordAuditOverview-newAudit"
                title={i18n.t('page.recordAudit.actions.newAudit')}
                variant="primary"
                onClick={() => navigate(recordAuditRoutes.create)}
              />
            ),
          },
        ]}
      >
        <DmsDataGrid
          key={linkedRecordId ?? 'all'}
          gridCode={RECORD_AUDIT_HISTORY_GRID_CODE}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
        />
      </Main>
    </>
  );
}
